// Angular and RJX Imports
// =========================================================
import { Component, ViewChild, type OnInit, type OnDestroy, HostListener } from '@angular/core'
import { cloneDeep } from 'lodash'
import { SuffixCellIconRenderer } from '../../../05_ag-grid-configs/03_custom-components/suffix-cell-icon-renderer/suffix-cell-icon-renderer'
import { CellClassRules } from '../../../05_ag-grid-configs/02_global-settings/cell-class-rules'
// Prime NG Imports
// =========================================================
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog'
// Ag Grid Imports
// =========================================================
import { type GridReadyEvent, type GridApi, ICellRendererParams, ColGroupDef, ColDef } from 'ag-grid-community'
// JSON Data
// =========================================================
import $storeIndexOutputGridJSON from '../../../05_ag-grid-configs/01_json-grid-configs/store-index/colDefs-main-store-index-output.json'
// Custom Imports
// =========================================================
import { StoreIndexOutputAPIs } from 'src/app/core/apis/store-index-output_api-calls'
import { CustomGridOptions, DefaultGridContextMenu } from 'src/app/05_ag-grid-configs/02_global-settings/grid-options'
import { StoreIndexOutputGrid_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/store-index-output-colDefs'
import { DefaultChartOptions, GenerateRandomColor, StandardChartColors } from 'src/app/utils/global-chart-settings'
import { TriggerApiCallsService } from 'src/app/core/services/cancel-api-call'
import { ChartModule, UIChart } from 'primeng/chart'
import { IsKeyInObj } from 'src/app/utils/global_functions'

@Component({
  standalone: false,
  selector: 'app-output-grid-data-modal',
  templateUrl: './output-grid-data-modal.component.html',
  styleUrls: ['./output-grid-data-modal.component.scss']
})
export class OutputGridDataModalComponent implements OnInit, OnDestroy {
  modalTitle: string = 'Store Index Output: '
  loading: boolean = true
  loadingMessage: string = 'loading'

  // Ag Grid Configuration
  private readonly customGridOptions: any = {
    ...CustomGridOptions,
    context: {
      componentParent: this,
      // columnsToAutoSize: ['source', 'index', 'store_id'],
      pageTitle: this.modalTitle,
      percentageTotal: 0,
      editedStores: []
    }
  }

  // Column Definitions JSON
  public storeIndexOutputGridJSON = $storeIndexOutputGridJSON
  // TODO: V2 Update: Add code to JSON Doc to enable populating the bar chart based on selection
  //   "Select": {
  //     "editable": false,
  //     "pinned": "left",
  //     "refData": {
  //         "datatype": "checkbox",
  //         "selectAll": true,
  //         "colSize": 80
  //     },
  //     "rowGroup": false,
  //     "hide": false,
  //     "spanHeaderHeight": true,
  //     "maxWidth": 60
  // },
  // TODO: V2 Update: ---------------------> DO NOT DELETE CODE BLOCK ABOVE (for JSON Doc)

  // Ag Grid Configuration
  gridApi: GridApi
  columnApi: any
  gridOptions: any = {}
  isAgGridLoading: boolean = true
  // Grid Data
  columnDefs: any[] = []

  parentGridData: any

  activeTab: Number = 0

  originalRowData: any
  currentRowData: any
  isRebalancedEnabled: boolean = false
  rebalanceExecuted: boolean = false
  isSaving: boolean = false
  selectedStoresData: any[] = []
  selectedStoresIDs: string[] = []
  updatedAttributes: any[] = []

  // doughnut Chart Vars
  doughnutChartData: any = {}
  doughnutChartConfig: any = {}
  doughnutChartOptions: any = {}
  // Bar Chart Vars
  barChartConfig: any = null
  barDatasets: any[] = [{}, {}]
  showDatasets: boolean[] = [true, true]
  barChartOptions: any = null

  selectedProductAttr: string = ''
  selectedFamilyAttr: string = ''

  familyAttributes: any[] = []
  productAttributes: string[] = []
  productAttributesToDisplay: string[] = []

  canvas: any
  ctx: any
  parentWidth: any

  @ViewChild('barChartAxis') barChartAxis
  @ViewChild('mainBarChartView') chartElement

  constructor (
    private readonly ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    private readonly storeIndexOutputAPIs: StoreIndexOutputAPIs,
    public triggerApiCallsService: TriggerApiCallsService
  ) {
    console.log('---> Store Index Output Modal: ', config)
    const { data } = config
    this.parentGridData = {
      ...data?.componentParent,
      rowData: data?.rowData.data,
      node: data?.rowData,
      index_ids: data?.index_ids
    }
    // Update modal title to include the job name
    this.modalTitle = this.modalTitle + this.parentGridData.rowData.job_name

    //  Set AG Grid Options
    this.gridOptions = {
      ...this.customGridOptions,
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      getRowId: node => node.data.store_id,
      getContextMenuItems: params => this.outputGridContextMenu(params),
      // TODO: V2 Update: Future Dev -> code to enable populating the bar chart based on selection
      // onSelectionChanged: (event: SelectionChangedEvent) => {
      //   this.selectedStoresData = event.api.getSelectedNodes()
      //   console.log(
      //     'row selection: changed: ',
      //     this.selectedStoresData
      //   )
      //   this.selectedStoresIDs =
      //               this.selectedStoresData.length > 0
      //                 ? this.selectedStoresData.map(
      //                   store => store.data.store_id
      //                 )
      //                 : []
      // },
      pinnedBottomRowData: [
        {
          store_id: null
        }
      ],
      onCellValueChanged: event => {
        console.log('---> cell value changed: ', event)
        this.updatedAttributes.push(event.colDef.field)
        event.data[`source_${event.colDef.field}`] = 'Pending'
        this.updatedAttributes = Array.from(new Set(this.updatedAttributes)) // Ensure unique attributes
        console.log('---> updatedAttributes: ', this.updatedAttributes)

        const newValue = Number(event.newValue)
        const oldValue = Number(Number(event.oldValue).toFixed(2))
        const id = event.node.id
        const attrField = event.colDef.colId

        if (newValue !== oldValue) {
          console.log('---> cell value changed - NO MATCH: ', event)
          const editKey = `${id}_${attrField}`
          if (!event.context.editedStores.includes(editKey)) {
            event.context.editedStores.push(editKey)
          }
          this.isRebalancedEnabled = true
          event.api.refreshCells({ force: true })
        } else if (newValue === oldValue) {
          console.log('---> cell value changed - MATCH: ', event)
        }

        this.calculateAttributeTotals()
      }
    }
  }

  ngOnInit () {
    let index_ids = []
    index_ids = this.parentGridData.index_ids
    if (index_ids.length === 0) {
      index_ids = Array.from(
        new Set([
          ...this.parentGridData.rowData.detail_data.map(detail => detail.index_id).filter(id => id != null)
        ])
      )
    }

    this.storeIndexOutputAPIs.GetStoreIndexOutputData(index_ids).then(res => {
      console.log('res: ==========>', res)
      this.productAttributes = Object.keys(res.rowData || {})
      console.log('Product attributes: ', this.productAttributes)

      this.originalRowData = this.transformRowData(res)

      this.doughnutChartData = res.chartData
      this.productAttributesToDisplay = Object.keys(this.doughnutChartData)
      console.log('Product Attributes to display: ', this.productAttributesToDisplay)
      this.selectedProductAttr = this.productAttributesToDisplay[0]
      this.familyAttributes = Object.keys(this.doughnutChartData[this.selectedProductAttr])
      console.log('Family Attributes: ', this.familyAttributes)
      this.selectedFamilyAttr = this.familyAttributes[0]

      // Generate hierarchical column structure
      const columnDefs = this.generateHierarchicalColumns(this.productAttributes)

      this.loading = false
      // this.configureChartTab()
      console.log('---> Original Row Data: ', this.originalRowData)

      this.renderGrid(columnDefs, this.originalRowData)
    })
  }

  onGridReady (event: GridReadyEvent) {
    this.gridApi = event.api
    this.columnApi = event.api
    this.isAgGridLoading = false
  }

  async renderGrid (columnDefs, rowData) {
    if (columnDefs) {
      this.columnDefs = columnDefs

      const checkAgGrid = () => {
        if (!this.isAgGridLoading) {
          if (this.gridApi && !this.gridApi['destroyCalled']) {
            this.gridApi.setGridOption('columnDefs', columnDefs)
            this.gridApi.setGridOption('rowData', rowData)

            this.calculateAttributeTotals()
          }
          clearInterval(setData)
          this.gridApi.refreshCells({ force: true })
          this.gridApi.sizeColumnsToFit()
        } else {
          console.log('Ag grid is loading')
        }
      }

      const setData = setInterval(checkAgGrid, 100)
    }
  }

  calculateAttributeTotals () {
    const totals = { store_id: 'Store Index (%) Total:' }
    const percentageTotal = []

    this.productAttributes.forEach(attr => {
      let total = 0

      this.gridApi.forEachNode(node => {
        if (
          node.data &&
                    node.data[attr] !== undefined &&
                    node.data[attr] !== null &&
                    !isNaN(Number(node.data[attr]))
        ) {
          total += Number(node.data[attr])
        }
      })

      const roundedTotal = Number(total.toFixed(2))
      totals[attr] = roundedTotal
      percentageTotal.push(roundedTotal)
      totals[`pct_to_avg_${attr}`] = null
    })
    const outlier = percentageTotal.find(val => val < 99.98 || val > 100.02)

    const percentageAverage =
            outlier !== undefined
              ? outlier
              : Number((percentageTotal.reduce((acc, val) => acc + val, 0) / percentageTotal.length).toFixed(2))
    console.log('---> Percentage Average: ', percentageAverage)
    // Update grid context
    this.gridApi.setGridOption('context', {
      ...this.gridApi.getGridOption('context'),
      percentageTotal: percentageAverage
    })

    // Update pinned bottom row
    this.gridApi.setGridOption('pinnedBottomRowData', [totals])
  }

  tabChanged (tabIndex) {
    console.log('--> Tab Changed Event: ', tabIndex)
    this.activeTab = tabIndex
    // Configure Data for the charts tab
    if (tabIndex === 1) {
      this.configureChartTab()
    }
  }

  transformRowData (res) {
    // Collect all unique store IDs
    const storeIDs = new Set()
    this.productAttributes.forEach(attr => {
      if (res.rowData[attr]) {
        res.rowData[attr].forEach(row => storeIDs.add(row.store_id))
      }
    })

    // Pre-compute medians for each attribute
    const attributeMedians = {}
    this.productAttributes.forEach(attr => {
      const rows = res.rowData[attr] || []
      const indexes = rows.map(r => r.index)
      indexes.sort((a, b) => a - b)
      const mid = Math.floor(indexes.length / 2)
      let median
      if (indexes.length === 0) {
        median = 1 // fallback
      } else if (indexes.length % 2 === 0) {
        median = (indexes[mid - 1] + indexes[mid]) / 2
      } else {
        median = indexes[mid]
      }
      attributeMedians[attr] = median
    })

    // Build transformed data
    const transformed = Array.from(storeIDs).map(storeId => {
      const row = { store_id: storeId }

      this.productAttributes.forEach(attr => {
        const found = res.rowData[attr]?.find(r => r.store_id === storeId)

        if (found) {
          const index = Number(found.index)
          row[`source_${attr}`] = found.source

          row[attr] = index
          row[`status_${attr}`] = found.status

          const median = attributeMedians[attr]
          row[`pct_to_avg_${attr}`] = median !== 0 ? index / median : '0.00'
        } else {
          row[attr] = 0
          row[`pct_to_avg_${attr}`] = 0
        }
      })

      return row
    })

    return transformed
  }

  generateHierarchicalColumns (attributes) {
    const columnDefs = [
      {
        headerName: 'PRODUCT ATTRIBUTES',
        children: [
          {
            headerName: 'STORES',
            field: 'store_id',
            pinned: 'left',
            lockPosition: true,
            editable: false,
            cellRenderer: 'agAnimateShowChangeCellRenderer',
            width: 120
          }
        ]
      },

      // Removed the "ALL" column group here

      ...attributes.map(attr => ({
        headerName: attr,
        children: [
          {
            headerName: 'PCT TO TTL',
            sort: 'desc',
            field: attr,
            editable: params => {
              console.log('---> PCT TO TTL Editable: ', params)
              const isPinned = params.node.rowPinned
              const locked = params?.data?.locked_indices?.[params.column.getColId()]
              const isApproved =
                                params?.data[`status_${params.column.getColId()}`].toLowerCase() === 'approved'
              const isSingleStore = this.originalRowData.length === 1
              return !isPinned && !locked && !isApproved && !isSingleStore
            },
            width: 100,
            valueFormatter: params => {
              return params.value != null ? `${Number(params.value).toFixed(2)}%` : '--'
            },
            valueGetter: params => {
              const val = params.data[attr]
              return val != null ? parseFloat(parseFloat(val).toFixed(2)) : '--'
            },
            cellClassRules: {
              ...CellClassRules,
              'cell-warning': params => {
                return (
                  params.node.rowPinned &&
                                    (Number(params?.value) <= 99.98 || Number(params?.value) >= 100.02)
                )
              },
              'cell-ok': params => {
                return (
                  params.node.rowPinned &&
                                    Number(params?.value) >= 99.98 &&
                                    Number(params?.value) <= 100.02
                )
              },
              'cell-text-bold': params => {
                if (
                  params.node.data[`source_${params.column.getColId()}`] !== 'System' ||
                                    typeof params.node.data[`${params.column.getColId()}`] === 'string'
                ) {
                  return true
                }
              },
              'cell-updated': params => {
                if (
                  params.node.data[`source_${params.column.getColId()}`] === 'Pending' ||
                                    typeof params.node.data[`${params.column.getColId()}`] === 'string'
                ) {
                  return true
                }
              }
            },
            cellRenderer: SuffixCellIconRenderer,
            cellRendererParams: {
              renderer_icon: 'lock',
              icon_key: 'locked_indices',
              action: 'object',
              static_icon: true
            }
          },
          {
            headerName: 'PCT TO AVG',
            field: `pct_to_avg_${attr}`,
            editable: false,
            width: 100,
            cellRenderer: params => {
              const val = params.value
              return val != null ? `${Number(val).toFixed(2)}%` : '--'
            }
          }
        ]
      }))
    ]

    return columnDefs
  }

  outputGridContextMenu (params) {
    console.log('---> Viewing Context Menu: ', params, params.column.getColDef().headerName)

    if (params.column) {
      const colId = params.column.colId

      // Locking and unlocking
      const LockUnlockGroup =
                params.column.getColDef().headerName === 'PCT TO TTL'
                  ? [
                      {
                        name: 'Lock / Unlock PCT To TTL',
                        subMenu: [
                          {
                            name:
                                          params?.node?.data?.locked_indices &&
                                          params?.node?.data?.locked_indices[colId]
                                            ? 'Unlock PCT To TTL'
                                            : 'Lock PCT To TTL',
                            action: async () =>
                              await this.onToggleLockUnlock(
                                params,
                                params?.node?.data?.locked_indices &&
                                                  params?.node?.data?.locked_indices[colId]
                                  ? 'unlock index'
                                  : 'lock index'
                              ),
                            cssClasses: ['text-accent']
                          },
                          {
                            name: 'Lock PCT To TTL Column',
                            action: async () => await this.onToggleLockUnlock(params, 'lock col'),
                            cssClasses: ['text-accent']
                          },
                          {
                            name: 'Unlock PCT To TTL Column',
                            action: async () => await this.onToggleLockUnlock(params, 'unlock col'),
                            cssClasses: ['text-accent']
                          },
                          {
                            name: 'Unlock All PCT To TTL Cells',
                            action: async () => await this.onToggleLockUnlock(params, 'unlock all'),
                            cssClasses: ['text-accent']
                          }
                        ],
                        disabled: this.loading
                      }
                    ]
                  : []

      const result = [...LockUnlockGroup, 'separator', ...DefaultGridContextMenu(params)]

      return result
    } else {
      return DefaultGridContextMenu(params)
    }
  }

  async onToggleLockUnlock (params: any, action: string) {
    this.gridApi.clearFocusedCell()
    console.log('---> Lock / Unlock: ', action, params.node.data)

    const lockedIndices = IsKeyInObj(params.node.data, 'locked_indices') ? params?.node.data?.locked_indices : {}
    const colIdToUpdate = params.column.colId
    const rowsToUpdate = []

    console.log('---> Toggling Lock / Unlock: ', params)
    // console.log('---> Action: ', action)
    switch (action) {
      case 'lock index':
      case 'unlock index':
        // if (params.node.data.store_id === '-1') {
        //   // find row to update
        //   const pinnedRowToUpdate = this.rowsToPinAtTop.find(row => row.row_id === params.node.data.row_id)
        //   pinnedRowToUpdate.locked_indices = {
        //     ...lockedIndices,
        //     [colIdToUpdate]: action === 'lock index'
        //   }
        //   this.refreshPinnedRows()
        //   return null
        // } else {
        const response = params.api.applyTransaction({
          update: [
            {
              ...params.node.data,
              locked_indices: {
                ...lockedIndices,
                [colIdToUpdate]: action === 'lock index'
              }
            }
          ]
        })
        this.gridApi.refreshCells({ force: true })
        return response
        // }
      case 'unlock all':
        try {
          // this.rowsToPinAtTop.forEach(row => {
          //   row.locked_indices = {}
          // })
          // this.refreshPinnedRows()
          await params.api.forEachNode(node => {
            const { data } = node

            if (data && IsKeyInObj(data, 'locked_indices')) {
              const valMap = [...new Set(Object.values(data?.locked_indices))]
              if (valMap.includes(true)) {
                rowsToUpdate.push({
                  ...data,
                  locked_indices: {}
                })
              }
            }
          })
        } finally {
          params.api.applyTransaction({ update: rowsToUpdate })
          this.gridApi.refreshCells({ force: true })
        }
        break
      case 'lock col':
      case 'unlock col':
        try {
          // if (action === 'lock col') {
          //   this.rowsToPinAtTop.forEach(row => {
          //     if (!row.locked_indices) {
          //       row.locked_indices = {}
          //     }
          //     row.locked_indices[colIdToUpdate] = true
          //   })
          // } else {
          //   this.rowsToPinAtTop.forEach(row => {
          //     if (row.locked_indices) {
          //       delete row.locked_indices[colIdToUpdate]
          //     }
          //   })
          // }
          // this.refreshPinnedRows()
          await params.api.forEachNode(node => {
            const { data } = node
            const lockedRowIndices = data?.locked_indices || {}
            const isColIndexLocked = lockedRowIndices[colIdToUpdate] || false
            if (
              (action === 'lock col' && !isColIndexLocked) ||
                            (action === 'unlock col' && isColIndexLocked)
            ) {
              rowsToUpdate.push({
                ...data,
                locked_indices: {
                  ...lockedRowIndices,
                  [colIdToUpdate]: !isColIndexLocked
                }
              })
            }
          })
        } catch (error) {
          console.error('---> error locking/unlocking all cols', error)
        } finally {
          params.api.applyTransaction({ update: rowsToUpdate })
          this.gridApi.refreshCells({ force: true })
        }
        break
    }
  }

  async onRebalanceRows (event) {
    console.log('---> Rebalancing Grid Rows', event)
    console.log('---> Rebalancing Attributes: ', this.updatedAttributes)

    this.gridApi.clearFocusedCell()
    const rowsToUpdate = []
    const lockedStores = []
    const allRows = []

    try {
      for (const attr of this.updatedAttributes) {
        console.log('looking at attr', attr)
        const unLockedStores = []
        let finalTotal = 0
        let totalUnlocked = 0
        let available = 100

        // First pass: gather unlocked/locked data
        const editedStores = this.gridApi.getGridOption('context').editedStores
        this.gridApi.forEachNode(row => {
          const data = row.data
          allRows.push(data)
          const value = Number(data[attr])

          const editKey = `${data.store_id}_${attr}`
          const isManuallyEdited = editedStores.includes(editKey)
          const isLocked = data.locked_indices?.hasOwnProperty(attr) && data.locked_indices[attr]

          if (!isManuallyEdited && !isLocked) {
            unLockedStores.push(data)
            totalUnlocked += value
          } else {
            lockedStores.push(data)
            available -= value
          }
        })

        console.log(`---> Attribute: ${attr}, Total Unlocked: ${totalUnlocked}, Available: ${available}`)

        if (unLockedStores.length > 1) {
          if (available <= 0) {
            console.warn(`Cannot rebalance ${attr}, locked values exceed 100`)
            continue
          }

          const ratio = available / totalUnlocked

          unLockedStores.forEach((store, sI) => {
            const originalVal = Number(store[attr])
            const newVal = parseFloat((originalVal * ratio).toFixed(4))
            finalTotal += newVal
            store[attr] = newVal

            // Add the remainder to the last store to fix rounding drift
            if (sI === unLockedStores.length - 1) {
              const remainder = parseFloat((available - finalTotal).toFixed(4))
              store[attr] = parseFloat((store[attr] + remainder).toFixed(4))
            }

            if (!rowsToUpdate.includes(store)) {
              rowsToUpdate.push(store)
            }
          })
        }
      }
    } catch (error) {
      console.error('---> Error Rebalancing Grid Rows', error)
    } finally {
      console.log('---> Final Rebalanced rows to Update', rowsToUpdate)
      if (rowsToUpdate.length > 0) {
        const attributeMedians = {}
        for (const attr of this.updatedAttributes) {
          const indexes = rowsToUpdate.map(r => r[attr])
          indexes.sort((a, b) => a - b)
          const mid = Math.floor(indexes.length / 2)
          let median
          if (indexes.length === 0) {
            median = 1 // fallback
          } else if (indexes.length % 2 === 0) {
            median = (indexes[mid - 1] + indexes[mid]) / 2
          } else {
            median = indexes[mid]
          }
          attributeMedians[attr] = median
        }

        // we want to update the pct to avg for locked stores too in case they were edited
        allRows.forEach(store => {
          this.updatedAttributes.forEach(attr => {
            const index = Number(store[attr])
            const median = attributeMedians[attr]

            const pctToAvg = median !== 0 ? index / median : '0.00'

            store[`pct_to_avg_${attr}`] = pctToAvg
          })
        })
        this.isRebalancedEnabled = false
        this.rebalanceExecuted = true
        this.gridOptions.context.editedStores = []
        this.gridApi.applyTransaction({ update: allRows })
        this.gridApi.refreshCells({ force: true })
      }
      this.calculateAttributeTotals()
    }
  }

  async configureChartTab () {
    console.log('---> configuring the chart tab: ', this.doughnutChartData)
    console.log('Selected Product Attribute: ', this.selectedProductAttr)
    const dataToCompare =
            this.selectedStoresData.length > 0
              ? this.originalRowData.filter(data => this.selectedStoresIDs.includes(data.store_id))
              : this.originalRowData

    const chartColors = {
      doughnut: [],
      originalBar: [],
      currentBar: []
    }
    const getColors = chart => {
      console.log('object vals: ', Object.values(chartColors))
      const color = GenerateRandomColor(Object.values(chartColors).map(val => val))
      chartColors[chart].push(color)
      return color
    }

    const barChartConfig = {
      labels: [],
      datasets: [
        {
          label: 'Original Value',
          order: 1,
          backgroundColor: StandardChartColors.accent_1,
          data: dataToCompare.map((store, i) => {
            return { y: store[this.selectedProductAttr], x: store.store_id }
          })
        },
        {
          label: 'Current Change',
          order: 2,
          backgroundColor: StandardChartColors.accent_2,
          data: []
        }
      ]
    }
    const doughnutChartConfig = {}
    doughnutChartConfig[this.selectedProductAttr] = {}
    this.familyAttributes.map(attr => {
      console.log('the attributes are: ', attr)
      doughnutChartConfig[this.selectedProductAttr][attr] = {
        labels: [],
        datasets: [
          {
            label: 'Doughnut',
            data: [],
            backgroundColor: [],
            hoverOffset: 4
          }
        ]
      }
    })

    try {
      if (this.selectedStoresData.length > 0) {
        // Update the current bar chart data with changes for the selected stores
        await this.selectedStoresData.forEach(store => {
          if (store?.data) {
            const { data } = store
            barChartConfig.datasets[1].data.push({
              y: data[this.selectedProductAttr],
              x: data?.store_id
            })
          }
        })
      } else {
        // Update the current bar chart data with changes for all stores
        await this.gridApi.forEachNode(node => {
          if (node?.data) {
            const { data } = node
            barChartConfig.datasets[1].data.push({
              y: data[this.selectedProductAttr],
              x: data?.store_id
            })
          }
        })
      }
      // Sort the bar chart's x-axis so by total value
      if (barChartConfig.datasets[0].data.length > 0 && barChartConfig.datasets[1].data.length > 0) {
        barChartConfig.labels = [
          ...new Set(
            [...barChartConfig.datasets[0].data, ...barChartConfig.datasets[1].data]
              .sort((a, b) => b.y - a.y)
              .map(store => store.x)
          )
        ]
      }
      this.familyAttributes.map(attr => {
        console.log('the attributes are: ', attr)
        console.log('this.doughnutChartData: ', this.doughnutChartData, this.selectedProductAttr, attr)
        // Update doughnut chart data
        this.doughnutChartData[this.selectedProductAttr][attr].forEach((data, i) => {
          doughnutChartConfig[this.selectedProductAttr][attr].labels.push(data.name)
          doughnutChartConfig[this.selectedProductAttr][attr].datasets[0].data.push(data.index)
          // If there are only 2 channels present -> use the standard grid colors
          if (this.doughnutChartData[this.selectedProductAttr][attr].length === 2) {
            doughnutChartConfig[this.selectedProductAttr][attr].datasets[0].backgroundColor = [
              StandardChartColors.secondary, // stores
              StandardChartColors.primary // channel
            ]
          } else {
            const color = getColors('doughnut')

            if (this.doughnutChartData[this.selectedProductAttr][attr].length === i + 1 && color) {
              doughnutChartConfig[this.selectedProductAttr][attr].datasets[0].backgroundColor =
                                chartColors['doughnut']
            }
          }
        })
      })
    } finally {
      this.familyAttributes.map(attr => {
        if (!this.doughnutChartConfig.hasOwnProperty(this.selectedProductAttr)) {
          this.doughnutChartConfig[this.selectedProductAttr] = {}
        }
        this.doughnutChartConfig[this.selectedProductAttr][attr] =
                    doughnutChartConfig[this.selectedProductAttr][attr]
        // Set Chart Options
        this.doughnutChartOptions[this.selectedProductAttr] = {}
        this.doughnutChartOptions[this.selectedProductAttr][attr] = {
          ...DefaultChartOptions,
          containerID: 'doughnut-chart',
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: {
                  size: 12,
                  family: "apple-system, 'Roboto', sans-serif"
                },
                generateLabels: chart => {
                  const visibility = []
                  chart.data.datasets.forEach((dataset, datasetIndex) => {
                    if (chart.getDatasetMeta(datasetIndex)) {
                      dataset.data.forEach((value, index) => {
                        const meta = chart.getDatasetMeta(datasetIndex)
                        visibility.push(!meta.data[index].hidden)
                      })
                    }
                  })

                  return chart.data.labels.map((label, index) => {
                    const style = visibility[index] ? 'normal' : 'line-through'
                    return {
                      text: label,
                      fillStyle: chart.data.datasets[0].backgroundColor[index],
                      hidden: !visibility[index],
                      lineThrough: !visibility[index],
                      style,
                      index
                    }
                  })
                }
              },
              onClick: (e, legendItem, legend) => {
                // Toggle visibility as normal
                const index = legendItem.index
                const ci = legend.chart
                const meta = ci.getDatasetMeta(0)
                const alreadyHidden = meta.data[index].hidden || false
                meta.data[index].hidden = !alreadyHidden

                // Force chart update
                ci.update()
              }
            },
            tooltip: {
              ...DefaultChartOptions.plugins.tooltip,
              callbacks: {
                ...DefaultChartOptions.plugins.tooltip.callbacks,
                title: context => {
                  return `${attr}: ${context[0].label}`
                },
                label: context => {
                  // Recalculate percentages based on currently visible data
                  const chart = context.chart
                  const datasetIndex = context.datasetIndex
                  const dataIndex = context.dataIndex
                  const meta = chart.getDatasetMeta(datasetIndex)

                  // Get values of all visible slices
                  let total = 0
                  meta.data.forEach((element, index) => {
                    if (!element.hidden) {
                      total += chart.data.datasets[datasetIndex].data[index]
                    }
                  })

                  // Calculate percentage based on visible total
                  const value = chart.data.datasets[datasetIndex].data[dataIndex]
                  const percentage = ((value / total) * 100).toFixed(2)

                  return `${context.label}: ${percentage}%`
                }
              }
            }
          }
        }
      })

      // Set the Bar Chart
      this.barDatasets[0] = barChartConfig.datasets[0]
      this.barDatasets[1] = barChartConfig.datasets[1]
      this.barChartConfig = barChartConfig
      this.parentWidth = barChartConfig.labels.length * 30
      console.log('chartelement', this.chartElement)

      // Set the bar chart options
      this.barChartOptions = {
        ...DefaultChartOptions,
        containerID: 'mainBarChart',
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          x: {
            categoryPercentage: 1.0,
            barPercentage: 1.0
          },
          y: {
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            ...DefaultChartOptions.plugins.tooltip,
            callbacks: {
              ...DefaultChartOptions.plugins.tooltip.callbacks,
              title: context => {
                // console.log('title: ', context)
                return `Store: ${context[0].label}`
              },
              label: context => {
                return `${this.barChartConfig.datasets[context.datasetIndex].label}: ${Number(
                                    context.formattedValue
                                ).toFixed(2)}`
              }
            }
          }
        }
        // animation: {
        //   onComplete: function () {
        //     // console.log('---> on complete1: ', this)

        //     const sourceCanvas = this.ctx.canvas
        //     const copyWidth = this.chartArea.left - 5
        //     const copyHeight = this.chartArea.bottom + 5

        //     const targetCtx = target.getContext('2d')
        //     targetCtx.canvas.width = copyWidth

        //     // Get the source size ratio by dividing the source height by 300 ( the original CSS height)
        //     const sourceSizeRatio = sourceCanvas.height / 300

        //     targetCtx.drawImage(
        //       sourceCanvas,
        //       0,
        //       0,
        //       copyWidth * sourceSizeRatio,
        //       copyHeight * sourceSizeRatio,
        //       0,
        //       0,
        //       copyWidth,
        //       copyHeight
        //     )
        //   }
        // }
      }

      // console.log('---> Chart Config: ', this.doughnutChartConfig);
    }
  }

  // Save Output changes
  async onSaveOutputChanges (event) {
    console.log('---> Saving Store Index Output Changes')
    const rowDataToSend = []
    this.isSaving = true

    try {
      await this.gridApi.forEachNode(row => {
        const { data } = row
        const storeData: any = {}
        storeData.job_name = this.parentGridData.rowData.job_name
        storeData.store_id = data.store_id
        for (const attr of this.updatedAttributes) {
          storeData[attr] = data[attr]
        }
        rowDataToSend.push(storeData)
        return data
      })
    } finally {
      // API CALL here
      console.log('---> Row Data to Send: ', rowDataToSend)
      this.storeIndexOutputAPIs
        .SaveStoreIndexOutputData({ row_data: rowDataToSend, updatedAttributes: this.updatedAttributes })
        .then(res => {
          if (res?.is_success) {
            this.rebalanceExecuted = false
            console.log('---> Success Saved Store Index Output Response: ', res)
            // this.close({
            //   message: 'success',
            //   data: rowDataToSend
            // })
          } else {
            // this.close()
            console.error('---> Error Saving Store Index Output Data: ', res)
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }

  onClickLegend (index: number) {
    console.log('clicked legend!', index)
    this.showDatasets[index] = !this.showDatasets[index]
    console.log('changing to', this.showDatasets[index])
    const id = index == 0 ? 'original-values-btn-wrapper' : 'current-changes-btn-wrapper'
    document.getElementById(id).style.textDecoration = this.showDatasets[index] ? '' : 'line-through'
    this.barChartConfig.datasets = this.recalcBarDatasets()
    this.barChartConfig = cloneDeep(this.barChartConfig)
  }

  recalcBarDatasets () {
    const datasets = []
    this.barDatasets.forEach((dataset, index) => {
      if (this.showDatasets[index]) datasets.push(dataset)
    })
    console.log('returning datasets', datasets)
    return datasets
  }

  onProductAttrSelectionChange (event) {
    console.log('---> Selection Changed: ', event)
    this.selectedProductAttr = event.value
    console.log('---> Selected', this.selectedProductAttr)
    this.familyAttributes = Object.keys(this.doughnutChartData[this.selectedProductAttr])
    console.log('Family Attributes: ', this.familyAttributes)
    this.selectedFamilyAttr = this.familyAttributes[0]
    this.configureChartTab()
  }

  onSelectionChange (event) {
    console.log('---> Selection Changed: ', event)
    this.selectedFamilyAttr = event.value
    console.log('---> Selected', this.selectedFamilyAttr)
  }

  close (data?: any) {
    this.triggerApiCallsService.onTriggerApiCalls({
      clear_api_calls: true
    })
    if (this.ref) this.ref.close(data || null)
  }

  @HostListener('unloaded')
  ngOnDestroy () {
    if (this.gridApi) {
      this.gridApi.flushAsyncTransactions()
      this.gridApi.expireValueCache()
      this.gridApi = null
    }
  }
}
